import axios from "axios";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apibaseurl, jwttoken } from "../../../environment";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";

// Define the types for the form data
interface FormData {
  name: string;
  email_id: string;
  phone_no: string;
  experience: string;
  position: string;
  resume: File | null;
  job_id: any;
}

const JobReferenceForm = () => {
  const location = useLocation();
  const navigate = useNavigate()
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email_id: "",
    phone_no: "",
    experience: "",
    position: location.state.job_title,
    resume: null,
    job_id: location.state.ID,
  });

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle file input changes (for resume upload)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      resume: e.target.files ? e.target.files[0] : null,
    });
  };

  const {
    data: referFriendResponse,
    loading: isSubmittingReferral,
    error: referFriendError,
    callApi: referFriendApi,
  } = useApi("/api/reffer-friend", {
    method: "POST",
    autoCall: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      form.classList.add("was-validated");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email_id", formData.email_id);
    formDataToSend.append("phone_no", formData.phone_no);
    formDataToSend.append("experience", formData.experience);
    formDataToSend.append("position", formData.position);
    formDataToSend.append("job_id", formData.job_id);

    if (formData.resume) {
      formDataToSend.append("resume", formData.resume);
    }

    try {
      const response = await referFriendApi(formDataToSend);

      Swal.fire({
        title: "Success!",
        text: response?.message || "Referral submitted successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      // Optionally reset form or state
      // form.reset(); or setFormData({ ... });

      form.classList.remove("was-validated");
      navigate("/job-refferals")
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text:
          referFriendError?.response?.data?.message ||
          "An unexpected error occurred.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="card">
            <div className="card-body p-3">
              <div className="d-flex align-items-center justify-content-center flex-column flex-md-row ">
                <h2 className="text-center">
                  Internal Job Posting / Refer Your Friend
                </h2>

                <div className="ms-md-2 mt-2 mt-md-0">
                  ({location.state.job_title})
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  {/* Name */}
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      <strong>Name:</strong>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter Full Name"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      <strong>Email ID:</strong>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="email_id"
                      name="email_id"
                      value={formData.email_id}
                      onChange={handleChange}
                      placeholder="Enter Email ID"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">
                      <strong>Phone No.:</strong>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone_no"
                      name="phone_no"
                      value={formData.phone_no}
                      onChange={handleChange}
                      placeholder="Enter Phone No."
                      required
                    />
                  </div>

                  {/* Years of Experience */}
                  <div className="mb-3">
                    <label htmlFor="experience" className="form-label">
                      <strong>Years of Experience:</strong>
                    </label>
                    <select
                      className="form-control"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Experience</option>
                      <option value="1-2">1-2 years</option>
                      <option value="2-3">2-3 years</option>
                      <option value="3-4">3-4 years</option>
                      <option value="4-5">4-5 years</option>
                      <option value="5-6">5-6 years</option>
                      <option value="6-7">6-7 years</option>
                      <option value="7-8">7-8 years</option>
                      <option value="8-9">8-9 years</option>
                      <option value="9-10">9-10 years</option>
                      <option value="10-11">10-11 years</option>
                      <option value="11-12">11-12 years</option>
                      <option value="12-13">12-13 years</option>
                      <option value="13-14">13-14 years</option>
                      <option value="14-15">14-15 years</option>
                    </select>
                  </div>

                  {/* Position */}
                  <div className="mb-3">
                    <label htmlFor="position" className="form-label">
                      <strong>Position:</strong>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="position"
                      name="position"
                      value={location.state.job_title}
                      onChange={handleChange}
                      placeholder="Enterprise Sales Manager"
                      disabled
                    />
                  </div>

                  {/* Resume Upload */}
                  <div className="mb-3">
                    <label htmlFor="resume" className="form-label">
                      <strong>Upload Resume:</strong>
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      accept=".pdf"
                    />
                    <small className="text-muted">
                      Upload PDF only and size should be less than or equal to 2
                      MB
                    </small>
                  </div>

                  {/* Submit Button */}
                  <div className="mb-3 text-end">
                    <button type="submit" className="btn btn-primary custom_button">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
    </>
  );
};

export default JobReferenceForm;
